import type { ReactNode } from 'react';

interface BreadcrumbItem {
  label: string;
  onClick?: () => void;
}

interface PageHeaderProps {
  breadcrumb?: BreadcrumbItem[];
  title: string;
  subtitle?: string;
  /** Right-side action buttons (e.g. Import button) */
  actions?: ReactNode;
}

/**
 * Standardised page-level header used across every dashboard view.
 * Renders a breadcrumb trail, large title, optional subtitle, and
 * optional right-aligned action slot.
 */
export function PageHeader({ breadcrumb, title, subtitle, actions }: PageHeaderProps) {
  return (
    <div className="flex flex-col gap-2 font-sans">
      {/* Breadcrumb */}
      {breadcrumb && breadcrumb.length > 0 && (
        <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">
          {breadcrumb.map((crumb, idx) => (
            <span key={idx} className="flex items-center gap-1.5">
              {idx > 0 && <span className="text-slate-300">/</span>}
              {crumb.onClick ? (
                <button
                  onClick={crumb.onClick}
                  className="hover:text-accent transition-colors cursor-pointer focus:outline-none"
                >
                  {crumb.label}
                </button>
              ) : (
                <span className="text-slate-600 font-extrabold">{crumb.label}</span>
              )}
            </span>
          ))}
        </div>
      )}

      {/* Title row */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="font-outfit font-extrabold text-3xl text-slate-900 tracking-tight leading-none">
            {title}
          </h1>
          {subtitle && (
            <p className="text-slate-450 text-xs font-semibold mt-2">{subtitle}</p>
          )}
        </div>
        {actions && (
          <div className="flex items-center gap-3 shrink-0">{actions}</div>
        )}
      </div>
    </div>
  );
}
